import { useState } from 'react';
import { INVENTORY_CATALOG } from '../inventory';
import type { DragAssemblyParams, DragAssemblyState } from '../types';

interface Props {
  params: DragAssemblyParams;
  puzzleState: DragAssemblyState;
  inventory: string[];
  onSolve: () => void;
  onGrantItem: (itemId: string) => void;
  patchState: (updater: (prev: DragAssemblyState) => DragAssemblyState) => void;
}

export function DragAssembly({ params, puzzleState, inventory, onSolve, onGrantItem, patchState }: Props) {
  const { slots, correctOrder, requiredItems, grantsItem } = params;
  const { slotContents, solved } = puzzleState;
  const [dragging, setDragging] = useState<string | null>(null);
  const [wrongPulse, setWrongPulse] = useState(false);

  const availableItems = requiredItems.filter(id => inventory.includes(id));
  const missingItems = requiredItems.filter(id => !inventory.includes(id));

  if (missingItems.length > 0) {
    return (
      <div className="puzzle-drag-assembly puzzle-drag-assembly--locked">
        <p className="puzzle-narrative">You are missing required items:</p>
        <ul className="puzzle-drag-assembly__missing">
          {missingItems.map(id => {
            const item = INVENTORY_CATALOG[id];
            return (
              <li key={id}>
                <i className={`fa-solid fa-${item?.icon ?? 'question'}`} aria-hidden="true" />
                {' '}{item?.label ?? id}
              </li>
            );
          })}
        </ul>
        <p className="puzzle-drag-assembly__tip">
          Collect the glyphs from Room 1 first.
        </p>
      </div>
    );
  }

  if (solved) {
    return (
      <p className="puzzle-solved-msg">
        <i className="fa-solid fa-circle-check" aria-hidden="true" /> Sigil assembled.
      </p>
    );
  }

  function handleDragStart(itemId: string) {
    setDragging(itemId);
  }

  function handleDrop(slotIndex: number) {
    if (!dragging) return;

    patchState(s => {
      const nextSlots = [...s.slotContents];
      // Remove dragged item from any other slot first
      const existingIdx = nextSlots.indexOf(dragging!);
      if (existingIdx !== -1) nextSlots[existingIdx] = null;
      nextSlots[slotIndex] = dragging!;

      // Check if complete
      const filled = nextSlots.every(v => v !== null);
      if (filled) {
        const correct = correctOrder.every((id, i) => nextSlots[i] === id);
        if (correct) {
          onGrantItem(grantsItem);
          onSolve();
          return { ...s, slotContents: nextSlots, solved: true };
        } else {
          setWrongPulse(true);
          setTimeout(() => setWrongPulse(false), 800);
        }
      }
      return { ...s, slotContents: nextSlots };
    });

    setDragging(null);
  }

  function removeFromSlot(slotIndex: number) {
    patchState(s => {
      const nextSlots = [...s.slotContents];
      nextSlots[slotIndex] = null;
      return { ...s, slotContents: nextSlots };
    });
  }

  const placedItems = slotContents.filter(Boolean);

  return (
    <div className="puzzle-drag-assembly">
      <p className="puzzle-narrative">
        Drag the glyphs from your inventory into the three slots in the correct order.
      </p>

      {/* Slots */}
      <div className={`puzzle-drag-assembly__slots${wrongPulse ? ' puzzle-drag-assembly__slots--wrong' : ''}`}>
        {Array.from({ length: slots }, (_, i) => {
          const itemId = slotContents[i];
          const item = itemId ? INVENTORY_CATALOG[itemId] : null;
          return (
            <div
              key={i}
              className={`puzzle-drag-assembly__slot${itemId ? ' puzzle-drag-assembly__slot--filled' : ''}`}
              onDragOver={e => { e.preventDefault(); }}
              onDrop={() => handleDrop(i)}
            >
              <span className="puzzle-drag-assembly__slot-num">{i + 1}</span>
              {item ? (
                <div className="puzzle-drag-assembly__slot-content">
                  <i className={`fa-solid fa-${item.icon}`} aria-hidden="true" />
                  <span>{item.label}</span>
                  <button
                    className="puzzle-drag-assembly__remove"
                    type="button"
                    aria-label={`Remove ${item.label} from slot ${i + 1}`}
                    onClick={() => removeFromSlot(i)}
                  >
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <span className="puzzle-drag-assembly__slot-empty">Drop here</span>
              )}
            </div>
          );
        })}
      </div>

      {wrongPulse && (
        <p className="puzzle-error-msg">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> Wrong order — try again.
        </p>
      )}

      {/* Draggable inventory chips */}
      <div className="puzzle-drag-assembly__inventory">
        <p className="puzzle-drag-assembly__inv-label">Your glyphs:</p>
        <div className="puzzle-drag-assembly__chips">
          {availableItems.map(id => {
            const item = INVENTORY_CATALOG[id];
            const isPlaced = placedItems.includes(id);
            return (
              <div
                key={id}
                className={`puzzle-drag-assembly__chip${isPlaced ? ' puzzle-drag-assembly__chip--placed' : ''}`}
                draggable={!isPlaced}
                onDragStart={() => !isPlaced && handleDragStart(id)}
              >
                <i className={`fa-solid fa-${item?.icon ?? 'question'}`} aria-hidden="true" />
                <span>{item?.label ?? id}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
